<script setup lang="ts">
import { computed, onMounted, ref } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import Alert from '@/components/v2/Alert.vue';
import Tabs from '@/components/v2/Tabs.vue';
import SubitemTypeTable from '@/components/SubitemTypeTable.vue';
import { policyTypeService } from '@/services/PolicyTypeService';
import { CoverageType } from '@/types/PolicyType';
import { SubitemKind, subitemKindLabels } from '@/types/PolicySublimit';

const route = useRoute();
const router = useRouter();

const tabs = (Object.keys(subitemKindLabels) as SubitemKind[]).map((kind) => ({
  value: kind,
  label: `${subitemKindLabels[kind]} Types`,
}));

const activeKind = computed<SubitemKind>({
  get: () => (route.query.tab === 'subretention' ? 'subretention' : 'sublimit'),
  set: (kind) => router.replace({ query: { ...route.query, tab: kind } }),
});

const coverageTypes = ref<CoverageType[]>([]);
const coverageTypesError = ref<string>();

onMounted(async () => {
  try {
    const loaded = await policyTypeService.GetDistinctCoverageTypes();
    coverageTypes.value = [...loaded].sort((a, b) => a.coverageType.localeCompare(b.coverageType));
  } catch {
    coverageTypesError.value = 'Failed to load coverage types.';
  }
});
</script>

<template>
  <div class="pt-4">
    <div class="inner py-0">
      <h1>Sublimit / Subretention Types</h1>
    </div>

    <div class="inner flex flex-col gap-4 pt-2">
      <div class="mx-auto w-11/12 mt-2 p-2.5 bg-blue-50 border border-solid border-blue-300 rounded-xl text-blue-950">
        Source key and source name reflect Epic's original label and are never overwritten. Editing the display name
        only changes what appears on policy lines and pickers across the platform.
      </div>

      <Alert
        v-if="coverageTypesError"
        type="error">
        {{ coverageTypesError }}
      </Alert>

      <Tabs
        v-model="activeKind"
        variant="lifted"
        :items="tabs">
        <template #content>
          <SubitemTypeTable
            :key="activeKind"
            class="mt-4"
            :kind="activeKind"
            :coverage-types="coverageTypes" />
        </template>
      </Tabs>

    </div>
  </div>
</template>
